import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import TimeAgo from 'react-timeago';
import axios from 'axios';
import EmojiEventsIcon  from '@material-ui/icons/EmojiEvents';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import FlagIcon         from '@material-ui/icons/Flag';
import GroupAddIcon     from '@material-ui/icons/GroupAdd';
import HelpOutlineIcon  from '@material-ui/icons/HelpOutline';
import MoodIcon         from '@material-ui/icons/Mood';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import StarIcon         from '@material-ui/icons/Star';
import RedeemIcon       from '@material-ui/icons/Redeem';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import { getUserEventHistory } from '@/services/program/getUserEvents';
import { getMerchants } from '@/services/program/getMerchants';
import { getGiftCodes } from '@/services/user/getGiftCodes';
import SocialWallPanel from './socialWall/SocialWallPanel';
import { USER_STATUS_PENDING_DEACTIVATION } from '@/services/user/getUser';

import { REDEEM_PATH } from '@/containers/Participant/GcmShared/routes';
import GiftCardImage from '@/shared/components/GiftCardImage';
const stripHtml = (value = '') =>
  value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();

const HomeDashboard = ({ auth, program, organization, pointBalance, template }) => {
  const { t } = useTranslation();
  const [activity, setActivity]           = useState([]);
  const [activityReady, setActivityReady] = useState(false);
  const [claimAwardCounts, setClaimAwardCounts] = useState(0);
  const [dynamicContentItems, setDynamicContentItems] = useState([]);
  const [giftCodes, setGiftCodes]         = useState([]);
  const [merchants, setMerchants]         = useState([]);
  const [merchantsReady, setMerchantsReady] = useState(false);
  const [socialWallReady, setSocialWallReady] = useState(false);
  const [socialWallHasPosts, setSocialWallHasPosts] = useState(false);
  const locale = new Intl.Locale('en-US');

  // refs declared here; effect is placed after quickLinks useMemo below
  const gridRef = React.useRef(null);
  const [gridMaxHeight, setGridMaxHeight] = React.useState('none');

  useEffect(() => {
    if (!organization?.id || !program?.id || !auth?.id) return;

    getUserEventHistory(organization.id, program.id, auth.id, 0, 5)
      .then(data => { setActivity(data.results || []); setActivityReady(true); })
      .catch(() => setActivityReady(true));

    setMerchantsReady(false);
    getMerchants(organization.id, program.id)
      .then(data => {
        const list = data?.merchants ?? (Array.isArray(data) ? data : []);
        const featured = list.filter(m => Number(m?.pivot?.featured) === 1 || Number(m?.featured) === 1);
        const rest     = list.filter(m => !featured.includes(m));
        setMerchants(featured.length > 0 ? featured.slice(0, 12) : rest.slice(0, 12));
        setMerchantsReady(true);
      })
      .catch(() => {
        setMerchants([]);
        setMerchantsReady(true);
      });

    getGiftCodes(organization.id, program.id, auth.id)
      .then(data => {
        setGiftCodes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setGiftCodes([]);
      });

    axios
      .get(`/organization/${program.organization_id}/program/${program.id}/user/${auth.id}/claim-award?countOnly=1`)
      .then((res) => {
        if (res.status === 200) {
          setClaimAwardCounts(Number(res.data) || 0);
        }
      })
      .catch(() => {
        setClaimAwardCounts(0);
      });

    axios
      .get(`/organization/${program.organization_id}/program/${program.id}/digital-media-type`)
      .then((res) => {
        const items = Array.isArray(res.data)
          ? res.data
              .map((row) => ({
                id: row.program_media_type_id,
                label: row.name,
                to:
                  Number(row.is_menu_item) === 1
                    ? `/participant/iframe/${row.program_media_type_id}`
                    : `/participant/media/${row.program_media_type_id}`,
              }))
          : [];
        setDynamicContentItems(items);
      })
      .catch(() => {
        setDynamicContentItems([]);
      });
  }, [organization?.id, program?.id, auth?.id]);

  const rawPts  = Math.floor(pointBalance?.pointBalance ?? 0);
  const pts     = rawPts.toLocaleString(locale);
  const peerRawPts = Math.floor(
    (Number(pointBalance?.peerBalance ?? 0) || 0) * (Number(pointBalance?.factor ?? 1) || 1)
  );
  const peerPts = peerRawPts.toLocaleString(locale);
  const heroImage = template?.hero_banner
    ? `${process.env.REACT_APP_API_STORAGE_URL}/${template.hero_banner}`
    : `${process.env.PUBLIC_URL}/theme/clear/img/back.jpg`;

  const factor          = program?.factor_valuation ? Number(program.factor_valuation) : null;
  const dollarValue     = factor ? Math.floor(rawPts / factor) : null;
  const nextMilestone   = factor ? Math.ceil((dollarValue + 0.01) / 25) * 25 : null;
  const nextMilestonePts = nextMilestone ? nextMilestone * factor : null;
  const ptsNeeded       = nextMilestonePts ? Math.max(0, nextMilestonePts - rawPts) : null;
  const progress        = nextMilestonePts ? Math.min(100, Math.round((rawPts / nextMilestonePts) * 100)) : null;

  const showSocialWall = (() => {
    if (!auth || !program) return false;
    if (auth.user_status_id === USER_STATUS_PENDING_DEACTIVATION && program.remove_social_from_pending_deactivation) return false;
    return program.uses_social_wall > 0;
  })();
  const hasNoPoints = rawPts <= 0;
  const hasNoActivity = activityReady && activity.length === 0;
  const showGettingStartedCard = hasNoActivity;
  const isNewUserDashboard = hasNoPoints && hasNoActivity;
  const showSocialWallSection = showSocialWall && (!socialWallReady || socialWallHasPosts);
  const hideSocialWallSection = hasNoActivity && !socialWallReady;
  const heroTitle = isNewUserDashboard
    ? t('dashboard_new_user_banner_title')
    : auth?.first_name
      ? `${t('welcome_back')}, ${auth.first_name}`
      : t('welcome_back');
  const heroMessage = isNewUserDashboard
    ? t('dashboard_new_user_banner_message')
    : stripHtml(template?.participant_homepage_message || t('participant_homepage_message'));
  const pointsCardLabel = hasNoPoints ? t('start_earning_today') : t('ready_to_redeem');
  const pointsCardSupport = hasNoPoints
    ? t('dashboard_no_points_support')
    : dollarValue === null
      ? t('points_available_to_spend')
      : null;
  const displayedMerchants = merchants.slice(0, 5);

  useEffect(() => {
    if (showSocialWall) {
      setSocialWallReady(false);
      setSocialWallHasPosts(false);
      return;
    }

    setSocialWallReady(true);
    setSocialWallHasPosts(false);
  }, [showSocialWall, organization?.id, program?.id]);

  const isGiftCodeRedemption = (item) => {
    const activityName = String(item?.name ?? '').toLowerCase();
    return activityName.includes('redeem') && activityName.includes('gift code');
  };

  const parseDateValue = (value) => {
    if (!value) return null;

    const normalizedValue = typeof value === 'string' ? value.replace(' ', 'T') : value;
    const parsedDate = new Date(normalizedValue);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const isSameCalendarDay = (leftDate, rightDate) => {
    if (!leftDate || !rightDate) return false;

    return (
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate()
    );
  };

  const getGiftCodeBaseAmount = (giftCode) => {
    const baseAmount = Number(
      giftCode?.redeemed_conversion_value ?? giftCode?.sku_value ?? 0
    );

    return Number.isFinite(baseAmount) ? Math.abs(baseAmount) : null;
  };

  const takeMatchingGiftCode = (availableGiftCodes, predicate) => {
    const matchIndex = availableGiftCodes.findIndex(predicate);

    if (matchIndex === -1) {
      return null;
    }

    const [matchedGiftCode] = availableGiftCodes.splice(matchIndex, 1);
    return matchedGiftCode;
  };

  const matchGiftCodeToActivity = (item, availableGiftCodes) => {
    const activityDate = parseDateValue(item?.journal_event_timestamp);
    const activityAmount = Math.abs(Number(item?.amount ?? 0));

    return (
      takeMatchingGiftCode(
        availableGiftCodes,
        (giftCode) =>
          isSameCalendarDay(activityDate, parseDateValue(giftCode?.redemption_date)) &&
          Math.abs((getGiftCodeBaseAmount(giftCode) ?? -1) - activityAmount) < 0.01
      ) ||
      takeMatchingGiftCode(
        availableGiftCodes,
        (giftCode) =>
          Math.abs((getGiftCodeBaseAmount(giftCode) ?? -1) - activityAmount) < 0.01
      ) ||
      takeMatchingGiftCode(
        availableGiftCodes,
        (giftCode) =>
          isSameCalendarDay(activityDate, parseDateValue(giftCode?.redemption_date))
      ) ||
      availableGiftCodes.shift() ||
      null
    );
  };

  const getMerchantRedemptionLabel = (giftCode) => {
    const merchantName = giftCode?.merchant?.name ?? giftCode?.merchant_name;
    return merchantName ? t('redeemed_from', { name: merchantName }) : t('redeemed_gift_code');
  };

  const getActivityIndicator = (item) => {
    const amount = Number(item?.amount ?? 0);

    if (isGiftCodeRedemption(item)) {
      return {
        className: 'hd__activity-pts hd__activity-pts--negative',
        label: `-${Math.round(Math.abs(amount) * (factor ?? 1)).toLocaleString(locale)} ${t('pts')}`,
      };
    }

    if (amount > 0) {
      return {
        className: 'hd__activity-pts',
        label: `+${Math.round(amount * (factor ?? 1)).toLocaleString(locale)} ${t('pts')}`,
      };
    }

    if (amount < 0) {
      return {
        className: 'hd__activity-pts hd__activity-pts--negative',
        label: `${Math.round(amount * (factor ?? 1)).toLocaleString(locale)} ${t('pts')}`,
      };
    }

    return {
      className: 'hd__activity-pts hd__activity-pts--zero',
      label: t('badge'),
    };
  };

  const matchedActivityRows = (() => {
    const availableGiftCodes = [...giftCodes].sort((left, right) => {
      const leftTime = parseDateValue(left?.redemption_date)?.getTime() ?? 0;
      const rightTime = parseDateValue(right?.redemption_date)?.getTime() ?? 0;
      return rightTime - leftTime;
    });

    return activity.map((item, index) => {
      const matchedGiftCode = isGiftCodeRedemption(item)
        ? matchGiftCodeToActivity(item, availableGiftCodes)
        : null;

      return {
        index,
        item,
        matchedGiftCode,
      };
    });
  })();

  const quickLinks = React.useMemo(() => {
    if (!program) return [];

    const items = [
      {
        key: 'browse-rewards',
        to: REDEEM_PATH,
        text: 'browse_rewards_cta',
        icon: <RedeemIcon />,
        color: '#f97316',
      },
      {
        key: 'my-points',
        to: '/participant/my-points',
        text: 'my_points',
        icon: <StarIcon />,
        color: '#2563eb',
      },
    ];

    if (Number(program.uses_peer2peer) > 0) {
      items.push({
        key: 'peer-to-peer',
        to: '/participant/peer-to-peer',
        text: 'give_an_award',
        icon: <CardGiftcardIcon />,
        color: '#2563eb',
      });
    }

    if (Number(program.uses_leaderboards) > 0) {
      items.push({
        key: 'leaderboards',
        to: '/participant/leaderboards',
        text: 'leaderboards',
        icon: <EmojiEventsIcon />,
        color: '#f59e0b',
      });
    }

    if (Number(program.uses_goal_tracker) > 0) {
      items.push({
        key: 'my-goals',
        to: '/participant/my-goals',
        text: 'my_goals',
        icon: <FlagIcon />,
        color: '#10b981',
      });
    }

    if (Number(program.enable_referrals) > 0) {
      items.push({
        key: 'referral',
        to: '/participant/referral',
        text: 'submit_a_referral',
        icon: <GroupAddIcon />,
        color: '#3b82f6',
      });
    }

    if (Number(program.enable_how_are_you_feeling) > 0) {
      items.push({
        key: 'feeling',
        to: '/participant/feeling',
        text: 'how_are_you_feeling',
        icon: <MoodIcon />,
        color: '#ef4444',
      });
    }

    if (Number(program.user_has_to_claim_award) > 0 || claimAwardCounts > 0) {
      items.push({
        key: 'claim-awards',
        to: '/participant/claim-awards',
        text: 'claim_awards',
        icon: <AssignmentTurnedInIcon />,
        color: '#06b6d4',
      });
    }

    dynamicContentItems.forEach((item) => {
      items.push({
        key: `dynamic-content-${item.id}`,
        to: item.to,
        label: item.label,
        icon: <RedeemIcon />,
        color: '#64748b',
      });
    });

    items.push({
      key: 'faqs',
      to: '/participant/faqs',
      text: 'faqs',
      icon: <HelpOutlineIcon />,
      color: '#64748b',
    });

    items.push({
      key: 'my-account',
      to: '/participant/my-account',
      text: 'my_account',
      icon: <PersonOutlineIcon />,
      color: '#0f766e',
    });

    return items;
  }, [program, claimAwardCounts, dynamicContentItems]);

  // ── Quick-links grid scroll ─────────────────────────────────────────────
  // Placed after quickLinks so it is already defined in scope.
  // requestAnimationFrame defers setState to the next paint, preventing the
  // "ResizeObserver loop completed with undelivered notifications" error that
  // fires when a layout change is triggered inside the observer callback.
  React.useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    let rafId = null;

    const recalculate = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        const firstTile = grid.querySelector('.hd__action-tile');
        if (!firstTile) return;

        const tileHeight = firstTile.getBoundingClientRect().height;
        const computedStyle = window.getComputedStyle(grid);
        const gap = parseFloat(computedStyle.rowGap) || 8;

        // Show exactly 3 rows; tiny peek of 4th row signals scrollability
        setGridMaxHeight(`${tileHeight * 3 + gap * 2}px`);
      });
    };

    recalculate();

    const ro = new ResizeObserver(recalculate);
    ro.observe(grid);

    return () => {
      ro.disconnect();
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [quickLinks]); // re-run when link list changes length
  // ───────────────────────────────────────────────────────────────────────

  const handleSocialWallStateChange = React.useCallback(({ ready, hasPosts }) => {
    setSocialWallReady(Boolean(ready));
    setSocialWallHasPosts(Boolean(hasPosts));
  }, []);

  const renderRecentActivitySection = (extraClassName = '') => (
    <div className={`hd__section hd__layout-panel${extraClassName ? ` ${extraClassName}` : ''}`}>
      <div className="hd__section-header">
        <span className="hd__section-title">{t('recent_activity')}</span>
        <Link to="/participant/my-points" className="hd__section-link">
          {t('see_all')} &rarr;
        </Link>
      </div>
      <div className="hd__activity">
        {!activityReady ? (
          <div className="hd__activity-empty">{t('loading')}</div>
        ) : activity.length === 0 ? (
          <div className="hd__activity-empty hd__activity-empty--action">
            <p className="hd__empty-title">{t('dashboard_empty_activity_title')}</p>
          </div>
        ) : (
          matchedActivityRows.map(({ item, index, matchedGiftCode }) => {
            const activityIndicator = getActivityIndicator(item);
            const activityLabel = isGiftCodeRedemption(item)
              ? getMerchantRedemptionLabel(matchedGiftCode)
              : item.name;

            return (
              <div key={index} className="hd__activity-row">
                <div className="hd__activity-indicator" />
                <div className="hd__activity-info">
                  <span className="hd__activity-name">{activityLabel}</span>
                  {item.first_name && (
                    <span className="hd__activity-from">
                      {item.first_name} {item.last_name}
                    </span>
                  )}
                </div>
                <div className="hd__activity-right">
                  {activityIndicator && (
                    <span className={activityIndicator.className}>
                      {activityIndicator.label}
                    </span>
                  )}
                  <span className="hd__activity-time">
                    <TimeAgo date={new Date(item.journal_event_timestamp.replace(' ', 'T') + 'Z')} />
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderMoreWaysToEarnSection = (extraClassName = '') => (
    <div className={`hd__sidebar-section hd__layout-panel${extraClassName ? ` ${extraClassName}` : ''}`}>
      <div className="hd__sidebar-label">{t('quick_links')}</div>
      {isNewUserDashboard && (
        <p className="hd__sidebar-copy">{t('dashboard_quick_links_intro')}</p>
      )}
      {quickLinks.length > 0 ? (
        <div
          ref={gridRef}
          className="hd__actions-grid"
          style={{
            maxHeight: gridMaxHeight,
            overflowY: gridMaxHeight === 'none' ? 'visible' : 'auto',
            overflowX: 'hidden',
            // Thin scrollbar for browsers that support it
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db transparent',
          }}
        >
          {quickLinks.map((item, i) => (
            <NavLink
              key={item.key || i}
              to={item.to}
              className="hd__action-tile"
              style={{ '--tile-color': item.color }}
            >
              <span className="hd__action-tile-icon">{item.icon}</span>
              <span className="hd__action-tile-label">
                {item.text ? t(item.text) : item.label}
              </span>
            </NavLink>
          ))}
        </div>
      ) : (
        <div className="hd__sidebar-empty">{t('loading')}</div>
      )}
    </div>
  );

  const renderGettingStartedSection = (extraClassName = '') => (
    <div className={`hd__section hd__section--guide hd__layout-panel${extraClassName ? ` ${extraClassName}` : ''}`}>
      <div className="hd__section-header">
        <span className="hd__section-title">{t('getting_started')}</span>
      </div>
      <div className="hd__guide">
        <p className="hd__guide-intro">{t('dashboard_getting_started_intro')}</p>
        <div className="hd__guide-steps">
          <div className="hd__guide-step">
            <span className="hd__guide-step-index">1</span>
            <span>{t('dashboard_getting_started_step_one')}</span>
          </div>
          <div className="hd__guide-step">
            <span className="hd__guide-step-index">2</span>
            <span>{t('dashboard_getting_started_step_two')}</span>
          </div>
          <div className="hd__guide-step">
            <span className="hd__guide-step-index">3</span>
            <span>{t('dashboard_getting_started_step_three')}</span>
          </div>
        </div>
        <Link to={REDEEM_PATH} className="hd__secondary-link">
          {t('browse_rewards_cta')} &rarr;
        </Link>
      </div>
    </div>
  );

  const renderTopRewardsSection = (extraClassName = '') => (
    <div className={`hd__sidebar-section hd__sidebar-section--merchants hd__sidebar-section--merchants-full hd__layout-panel${extraClassName ? ` ${extraClassName}` : ''}`}>
      <div className="hd__sidebar-label-row">
        <span className="hd__sidebar-label">{t('top_rewards_for_you')}</span>
        <Link to={REDEEM_PATH} className="hd__section-link hd__section-link--featured">
          {t('browse_all')} &rarr;
        </Link>
      </div>

      {!merchantsReady ? (
        <div className="hd__sidebar-empty">{t('loading')}</div>
      ) : displayedMerchants.length > 0 ? (
        <div className="hd__merchants hd__merchants--featured">
          {displayedMerchants.map(m => (
            <Link
              key={m.id}
              to={`/participant/redeem/${m.id}`}
              className="hd__merchant-card"
            >
              <div className="hd__merchant-logo">
                <GiftCardImage
                  src={m.logo ? `${process.env.REACT_APP_API_STORAGE_URL}/${m.logo}` : ''}
                  name={m.name}
                />
              </div>
              <span className="hd__merchant-name">{m.name}</span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="hd__sidebar-empty">
          <p className="hd__sidebar-empty-text">{t('dashboard_rewards_empty')}</p>
          <Link to={REDEEM_PATH} className="hd__secondary-link hd__secondary-link--left">
            {t('browse_rewards_cta')} &rarr;
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="hd">
      {/* ── Dashboard body ───────────────────────────────────────────────── */}
      <Container className="hd__body">
        <div className="hd__hero-card">
          <div
            className="hd__hero-backdrop"
            style={{ backgroundImage: `url("${heroImage}")` }}
            aria-hidden="true"
          />
          <img src={heroImage} alt={program?.name || 'Program banner'} className="hd__hero-img" />
          <div className="hd__hero-overlay">
            <div className="hd__hero-copy">
              {program?.name && (
                <span className="hd__hero-kicker">{program.name}</span>
              )}
              <h2 className="hd__hero-title">{heroTitle}</h2>
              {heroMessage && (
                <p className="hd__hero-desc">{heroMessage}</p>
              )}
            </div>
          </div>
        </div>

        <Row className="hd__layout-row">
          <Col xs={12} md={4} className="hd__layout-col">
            <div className="hd__layout-stack">
              <div className="hd-card hd-card--rewards hd__layout-panel">
                <div className="hd-card__eyebrow">{t('available_points')}</div>
                <div className="hd-card__balance">{pts}</div>
                <div className="hd-card__pts-label">{pointsCardLabel}</div>
                {pointsCardSupport && (
                  <div className="hd-card__support-text">{pointsCardSupport}</div>
                )}
                <Link to={REDEEM_PATH} className="hd-card__btn hd-card__btn--cta">
                  <RedeemIcon style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }} />
                  {t('browse_rewards_cta')}
                </Link>
                {progress !== null && progress < 100 && (
                  <div className="hd-progress">
                    <div className="hd-progress__label">
                      {t('progress_milestone', { pts: ptsNeeded.toLocaleString(locale), amount: nextMilestone })}
                    </div>
                    <div className="hd-progress__track">
                      <div className="hd-progress__fill" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                )}
                {Number(program?.uses_peer2peer) > 0 && (
                  <div className="hd-card__peer-section">
                    <div className="hd-card__peer-give-text">
                      <CardGiftcardIcon className="hd-card__peer-give-icon" />
                      <span>{t('send_pts_to_teammate', { pts: peerPts })}</span>
                    </div>
                    <Link to="/participant/peer-to-peer" className="hd-card__btn hd-card__btn--peer">
                      <CardGiftcardIcon style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }} />
                      {t('send_points')}
                    </Link>
                  </div>
                )}
              </div>
              {renderMoreWaysToEarnSection('hd__layout-panel--fill')}
            </div>
          </Col>

          <Col xs={12} md={8} className="hd__layout-col">
            <div className="hd__layout-stack">
              {renderTopRewardsSection()}
              {renderRecentActivitySection('hd__layout-panel--fill')}
              {showGettingStartedCard ? renderGettingStartedSection() : null}
            </div>
          </Col>
        </Row>

        {showSocialWallSection ? (
          <Row className={`hd__layout-row${hideSocialWallSection ? ' hd__layout-row--hidden' : ''}`}>
            <Col xs={12} className="hd__layout-col">
              <div className="hd__section hd__layout-panel">
                <div className="hd__section-header">
                  <span className="hd__section-title">{t('social_wall')}</span>
                </div>
                <SocialWallPanel
                  emptyStateMessage={t('no_social_posts_yet')}
                  onStateChange={handleSocialWallStateChange}
                />
              </div>
            </Col>
          </Row>
        ) : null}
      </Container>
    </div>
  );
};

const mapStateToProps = state => ({
  auth:         state.auth,
  program:      state.program,
  organization: state.organization,
  pointBalance: state.pointBalance,
  template:     state.template,
});

export default connect(mapStateToProps)(HomeDashboard);
